import type { AdminCommunityPostPage, AdminCommunityPostSummary } from '@inflip/contracts/admin';
import { COMMUNITY_POST_CATEGORY_LABELS } from '@inflip/shared';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { DataTable } from '../components/data-table';
import {
  Button,
  Chip,
  EmptyState,
  ErrorBanner,
  inputCls,
  LoadingRow,
  PageHeader,
} from '../components/ui';
import { api } from '../lib/api-client';
import { useAdminAuth } from '../lib/auth-context';
import { formatDateTime } from '../lib/time-zone';
import { useListViewState } from '../lib/use-list-view-state';

const PAGE_SIZE = 20;

type StatusFilter = 'ACTIVE' | 'DELETED' | 'ALL';
const STATUS_CHIPS: { key: StatusFilter; label: string }[] = [
  { key: 'ACTIVE', label: '게시 중' },
  { key: 'DELETED', label: '삭제됨' },
  { key: 'ALL', label: '전체' },
];

const COLUMNS: ColumnDef<AdminCommunityPostSummary>[] = [
  {
    id: 'category',
    header: '말머리',
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {COMMUNITY_POST_CATEGORY_LABELS[row.original.category]}
      </span>
    ),
  },
  {
    id: 'title',
    header: '제목',
    cell: ({ row }) => (
      <span className="flex min-w-0 items-center gap-1.5">
        <Link
          to="/community/$postId"
          params={{ postId: row.original.id }}
          className="truncate font-medium text-brand hover:underline"
        >
          {row.original.title}
        </Link>
        {row.original.deletedAt && (
          <span className="shrink-0 text-xs font-bold text-muted-foreground">삭제됨</span>
        )}
      </span>
    ),
  },
  {
    id: 'author',
    header: '작성자',
    cell: ({ row }) => (
      <Link
        to="/users/$userId"
        params={{ userId: row.original.author.id }}
        className="whitespace-nowrap hover:underline"
      >
        {row.original.author.name ?? '(탈퇴)'}
      </Link>
    ),
    meta: { tdClass: 'text-xs' },
  },
  {
    id: 'comments',
    header: '댓글',
    cell: ({ row }) => row.original.commentCount,
    meta: { tdClass: 'text-xs tabular-nums' },
  },
  {
    id: 'likes',
    header: '좋아요',
    cell: ({ row }) => row.original.likeCount,
    meta: { tdClass: 'text-xs tabular-nums' },
  },
  {
    id: 'reports',
    header: '대기 신고',
    cell: ({ row }) =>
      row.original.pendingReportCount > 0 ? (
        <span className="font-bold text-rose-600">{row.original.pendingReportCount}건</span>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
    meta: { tdClass: 'text-xs tabular-nums' },
  },
  {
    id: 'createdAt',
    header: '작성',
    cell: ({ row }) => formatDateTime(row.original.createdAt),
    meta: { tdClass: 'whitespace-nowrap text-xs text-muted-foreground' },
  },
];

/** 커뮤니티 글 — 익명 글을 실제 작성자와 함께 둘러본다. 삭제는 상세에서 */
export function CommunityPostsPage() {
  const { session } = useAdminAuth();
  // 목록 상태는 URL 에 — 상세에서 뒤로 나와도 보던 페이지·필터가 유지된다
  const [status, setStatus] = useListViewState<StatusFilter>('status', 'ACTIVE');
  const [searchTerm, setSearchTerm] = useListViewState('q', '');
  const [q, setQ] = useState(searchTerm);
  const [page, setPage] = useListViewState('page', 1);
  const [result, setResult] = useState<AdminCommunityPostPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listCommunityPosts(session.accessToken, {
        page,
        pageSize: PAGE_SIZE,
        status,
        ...(searchTerm ? { q: searchTerm } : {}),
      })
      .then((data) => !cancelled && setResult(data))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : String(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [session, status, searchTerm, page]);

  if (!session) return null;
  const rows = result?.items ?? [];

  return (
    <main className="w-full px-6 py-8">
      <PageHeader
        title="커뮤니티 글"
        description={
          result
            ? `${result.total.toLocaleString('ko-KR')}개 · 익명 글의 실제 작성자를 볼 수 있어요`
            : '익명 글의 실제 작성자를 볼 수 있어요'
        }
        actions={
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setSearchTerm(q.trim());
            }}
          >
            <input
              className={`w-64 ${inputCls}`}
              placeholder="제목 / 본문 검색"
              maxLength={50}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button type="submit">검색</Button>
          </form>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {STATUS_CHIPS.map((c) => (
          <Chip
            key={c.key}
            active={status === c.key}
            onClick={() => {
              setPage(1);
              setStatus(c.key);
            }}
          >
            {c.label}
          </Chip>
        ))}
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && !result && <LoadingRow />}

      {result && rows.length === 0 ? (
        <EmptyState message={searchTerm ? '검색 결과가 없어요.' : '글이 없어요.'} />
      ) : (
        <DataTable data={rows} columns={COLUMNS} getRowId={(r) => r.id} />
      )}

      {result && result.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage(page - 1)}
          >
            이전
          </Button>
          <span className="text-sm tabular-nums text-muted-foreground">
            {result.page} / {result.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= result.totalPages || loading}
            onClick={() => setPage(page + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </main>
  );
}
